import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ExternalLink } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { WhatsAppFloat } from "../components/WhatsAppFloat";
import { resolveImageUrl } from "../components/ImageUploader";
import { useReveal } from "../hooks/useReveal";
import { useI18n } from "../context/I18nContext";
import { getReferences, getSettings } from "../lib/store";

export default function ReferencesPage() {
  const { t } = useI18n();
  const [settings, setSettings] = useState(getSettings);
  const [items, setItems]       = useState(() => getReferences().filter(r => r.active !== false));

  useEffect(() => {
    setSettings(getSettings());
    setItems(getReferences().filter(r => r.active !== false));
    window.scrollTo(0, 0);
  }, []);

  useReveal();

  return (
    <div className="App">
      <Navbar />

      {/* Hero band */}
      <section className="bg-[#0d1b2a] text-white py-20 lg:py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" />
        <div className="relative max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-white/50 hover:text-[#ffb703] transition-colors mb-8"
          >
            <ArrowLeft size={14} /> Ana Sayfa
          </Link>
          <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#ffb703] mb-3">
            {t.references.eyebrow}
          </div>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight"
            style={{ fontFamily: "Chivo, Inter, sans-serif" }}
          >
            {t.references.heading}
          </h1>
          <p className="mt-5 text-lg text-white/70 max-w-2xl leading-relaxed">
            {t.references.sub}
          </p>
        </div>
      </section>

      {/* References grid */}
      <section className="py-16 lg:py-24 bg-[#f6f6f6]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          {items.length === 0 ? (
            <div className="py-24 text-center text-[#0d1b2a]/40 text-sm">
              Henüz referans eklenmedi.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {items.map((r, i) => (
                <article
                  key={r.id || i}
                  data-reveal
                  style={{ transitionDelay: `${i * 40}ms` }}
                  className="bg-white border border-[#0d1b2a]/10 rounded-2xl overflow-hidden hover:-translate-y-1 hover:shadow-[0_8px_32px_rgba(13,27,42,0.08)] transition-all duration-300 flex flex-col"
                >
                  {/* Proje görseli */}
                  <div className="aspect-[16/9] bg-[#0d1b2a] overflow-hidden relative">
                    {r.project_image ? (
                      <img
                        src={resolveImageUrl(r.project_image)}
                        alt={r.name}
                        className="w-full h-full object-cover opacity-90"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-white/20 text-sm">Görsel yok</span>
                      </div>
                    )}
                    {/* Logo overlay */}
                    {r.logo_url && (
                      <div className="absolute bottom-3 left-3 bg-white/95 backdrop-blur-sm rounded-lg px-3 py-2 shadow-lg">
                        <img
                          src={resolveImageUrl(r.logo_url)}
                          alt={r.name}
                          className="h-9 w-auto max-w-[120px] object-contain"
                        />
                      </div>
                    )}
                  </div>

                  {/* İçerik */}
                  <div className="p-6 flex-1 flex flex-col">
                    <h3
                      className="text-lg font-black text-[#0d1b2a]"
                      style={{ fontFamily: "Chivo, Inter, sans-serif" }}
                    >
                      {r.name}
                    </h3>

                    {r.description && (
                      <p className="mt-2 text-sm text-[#0d1b2a]/70 leading-relaxed flex-1">
                        {r.description}
                      </p>
                    )}

                    {r.website && (
                      <a
                        href={r.website}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold text-[#0d1b2a]/50 hover:text-[#ffb703] transition-colors"
                      >
                        <ExternalLink size={12} />
                        Web sitesini ziyaret et
                      </a>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer settings={settings} />
      <WhatsAppFloat settings={settings} />
    </div>
  );
}
